import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { resolveCustomDefaults } from "@/config/endpoints";
import { useMemo } from "react";

type ModuleScope = { nicheModelName: string; nicheItemId: number };

type ResolvedOption = { id: number; value: string };

export type ResolvedField = {
  customInputId: number;
  resolvedOptions: ResolvedOption[];
  autoFill: boolean;
};

export default function useResolveCustomDefaults(
  moduleScopes: ModuleScope[] = [],
  targetModel: string = "child"
) {
  const stableKey = useMemo(() => JSON.stringify(moduleScopes), [moduleScopes]);

  const hasValidScopes =
    moduleScopes.length > 0 &&
    moduleScopes.every((s) => s.nicheModelName && s.nicheItemId);

  const query = useQuery<ResolvedField[]>({
    queryKey: ["resolve-custom-defaults", targetModel, stableKey],
    queryFn: async () => {
      const res = await axiosInstance.post(resolveCustomDefaults, {
        moduleScopes,
        targetModel,
      });
      return res.data.resolvedFields ?? [];
    },
    enabled: hasValidScopes,
  });

  // Keep an empty fallback reference stable so consumers with deps on
  // `resolvedFields` (e.g. PrefrenceFormRenderer's align effect) don't
  // see a new `[]` on every render before data arrives — that combined
  // with a parent watcher on `customInputScopes` caused an infinite
  // setValue → re-render → setValue loop (React error #185).
  const resolvedFields = query.data ?? EMPTY;

  return {
    resolvedFields,
    isLoading: query.isLoading,
    error: query.error,
  };
}

const EMPTY: ResolvedField[] = [];
